import { useState, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, Maximize2, Share2, MessageCircle, Send, Mail, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { mediaSrc } from "@/lib/media";

const CAT_ORDER = ["corniches", "moulures", "colonnes", "interieurs"];

const ShareMenu = ({ item, testid }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const url = typeof window !== "undefined" ? window.location.origin : "";
  const text = item?.title ? `${item.title} — A&K Atelier` : "A&K Atelier — Décors en plâtre sur mesure";
  const enc = encodeURIComponent;
  const stop = (e) => e.stopPropagation();
  const go = (e, href) => {
    stop(e);
    window.open(href, "_blank", "noopener");
    setOpen(false);
  };
  const copy = async (e) => {
    stop(e);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("portfolio.shareCopied"));
    } catch {
      /* ignore */
    }
    setOpen(false);
  };
  const opts = [
    { key: "wa", label: "WhatsApp", Icon: MessageCircle, onClick: (e) => go(e, `https://wa.me/?text=${enc(`${text} ${url}`)}`) },
    { key: "tg", label: "Telegram", Icon: Send, onClick: (e) => go(e, `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`) },
    { key: "em", label: "E-mail", Icon: Mail, onClick: (e) => go(e, `mailto:?subject=${enc("A&K Atelier")}&body=${enc(`${text} ${url}`)}`) },
    { key: "cp", label: t("portfolio.shareCopy"), Icon: Link2, onClick: copy },
  ];

  return (
    <div className="absolute top-3 right-3 z-30" onClick={stop}>
      <button
        type="button"
        data-testid={testid}
        aria-label={t("portfolio.share")}
        onClick={(e) => {
          stop(e);
          setOpen((o) => !o);
        }}
        className="flex items-center justify-center w-9 h-9 rounded-full bg-black/55 backdrop-blur text-white opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:bg-gold hover:text-black transition-all duration-300"
      >
        <Share2 size={16} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={(e) => { stop(e); setOpen(false); }} />
          <div
            data-testid={`${testid}-menu`}
            className="absolute right-0 mt-2 w-48 z-20 rounded-md border border-border bg-card shadow-2xl overflow-hidden"
          >
            {opts.map((o) => (
              <button
                key={o.key}
                type="button"
                data-testid={`${testid}-${o.key}`}
                onClick={o.onClick}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-gold hover:text-black transition-colors text-left"
              >
                <o.Icon size={15} /> {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Portfolio = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("photos");
  const [cat, setCat] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/portfolio").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const photos = useMemo(() => items.filter((i) => i.type === "photo"), [items]);
  const videos = useMemo(() => items.filter((i) => i.type === "video"), [items]);

  const categories = useMemo(() => {
    const present = new Set(photos.map((p) => p.category));
    return ["all", ...CAT_ORDER.filter((c) => present.has(c))];
  }, [photos]);

  const filtered = useMemo(
    () => (cat === "all" ? photos : photos.filter((p) => p.category === cat)),
    [cat, photos],
  );

  const close = useCallback(() => setLightbox(null), []);
  const next = useCallback(
    () => setLightbox((lb) => (lb ? { ...lb, index: (lb.index + 1) % filtered.length } : lb)),
    [filtered.length],
  );
  const prev = useCallback(
    () => setLightbox((lb) => (lb ? { ...lb, index: (lb.index - 1 + filtered.length) % filtered.length } : lb)),
    [filtered.length],
  );

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (lightbox.type === "photo" && e.key === "ArrowRight") next();
      if (lightbox.type === "photo" && e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, next, prev]);

  const catLabel = (c) => {
    const label = t(`portfolio.cats.${c}`);
    return label && !label.startsWith("portfolio.") ? label : c;
  };

  return (
    <section id="portfolio" className="relative py-16 md:py-36 px-6 md:px-12 bg-background" style={{ scrollMarginTop: 80 }}>
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="max-w-2xl">
            <Eyebrow>{t("portfolio.badge")}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl text-foreground leading-tight">
              {t("portfolio.title")}
            </h2>
            <p className="mt-5 text-muted-foreground font-sans leading-relaxed">{t("portfolio.subtitle")}</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 inline-flex border border-border rounded-sm overflow-hidden">
            {[
              { id: "photos", label: t("portfolio.photos") },
              { id: "videos", label: t("portfolio.videos") },
            ].map((tb) => (
              <button
                key={tb.id}
                data-testid={`portfolio-tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`px-7 py-3 text-xs font-sans uppercase tracking-[0.18em] transition-colors ${
                  tab === tb.id ? "bg-gold text-black" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tb.label}
              </button>
            ))}
          </div>
        </Reveal>

        {tab === "photos" && categories.length > 1 && (
          <Reveal delay={0.15}>
            <div className="mt-6 flex flex-wrap gap-2.5" data-testid="portfolio-categories">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`portfolio-cat-${c}`}
                  onClick={() => setCat(c)}
                  className={`px-5 py-2 text-xs font-sans uppercase tracking-[0.14em] rounded-full border transition-all duration-300 ${
                    cat === c
                      ? "border-gold text-gold bg-gold/10"
                      : "border-border text-muted-foreground hover:border-gold/50 hover:text-foreground"
                  }`}
                >
                  {catLabel(c)}
                </button>
              ))}
            </div>
          </Reveal>
        )}

        {tab === "photos" && (
          <div className="mt-10 masonry" data-testid="portfolio-photos">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <motion.div
                  key={p.item_id}
                  layout
                  data-testid={`photo-${i}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4 }}
                  className="group relative w-full"
                >
                  <ShareMenu item={p} testid={`share-photo-${i}`} />
                  <button
                    type="button"
                    onClick={() => setLightbox({ type: "photo", index: i })}
                    className="relative w-full overflow-hidden block"
                  >
                    <img
                      src={mediaSrc(p.media_url)}
                      alt={p.title || `Réalisation ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {p.title ? (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="font-serif text-sm text-white">{p.title}</span>
                      </div>
                    ) : null}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <Maximize2 size={26} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {tab === "videos" && (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="portfolio-videos">
            {videos.map((v, i) => (
              <motion.div
                key={v.item_id}
                data-testid={`video-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="group relative"
              >
                <ShareMenu item={v} testid={`share-video-${i}`} />
                <button
                  type="button"
                  onClick={() => setLightbox({ type: "video", index: i })}
                  className="relative w-full aspect-[9/12] bg-card border border-border hover:border-gold/50 overflow-hidden transition-colors block"
                >
                  <video
                    src={`${mediaSrc(v.media_url)}#t=0.5`}
                    preload="metadata"
                    muted
                    playsInline
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  {v.title ? (
                    <span className="absolute bottom-3 left-3 right-14 font-serif text-sm text-white text-left">{v.title}</span>
                  ) : null}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex items-center justify-center w-16 h-16 rounded-full bg-gold/90 text-black group-hover:scale-110 transition-transform duration-300">
                      <Play size={24} className="ml-1" fill="currentColor" />
                    </span>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              className="absolute top-5 right-5 text-white/70 hover:text-gold transition-colors z-10"
            >
              <X size={30} />
            </button>

            {lightbox.type === "photo" && (
              <>
                <button
                  data-testid="lightbox-prev"
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute left-3 md:left-8 text-white/60 hover:text-gold transition-colors z-10"
                >
                  <ChevronLeft size={44} />
                </button>
                <motion.img
                  key={lightbox.index}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  src={mediaSrc(filtered[lightbox.index]?.media_url)}
                  alt={filtered[lightbox.index]?.title || ""}
                  onClick={(e) => e.stopPropagation()}
                  className="max-h-[88vh] max-w-[88vw] object-contain"
                />
                <button
                  data-testid="lightbox-next"
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute right-3 md:right-8 text-white/60 hover:text-gold transition-colors z-10"
                >
                  <ChevronRight size={44} />
                </button>
              </>
            )}

            {lightbox.type === "video" && (
              <video
                key={lightbox.index}
                src={mediaSrc(videos[lightbox.index]?.media_url)}
                controls
                autoPlay
                playsInline
                onClick={(e) => e.stopPropagation()}
                className="max-h-[88vh] max-w-[92vw] md:max-w-[480px] object-contain rounded-sm"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
